import { useEffect, useMemo } from 'react'

import { useAppSelector } from 'store'
import { getMapComponent } from 'utils'
import { TChannelVariant } from 'interfaces'
import { useUnityHubCreateChannel, TUseUnityHubCreateChannel } from 'App/components/UnityHub'

import {
  EventCreateChannelMainStep,
  TEventCreateChannelMainStepProps
} from '../EventCreateChannelMainStep'
import {
  EventCreateChannelMembersStep,
  TEventCreateChannelMembersStepProps
} from '../EventCreateChannelMembersStep'
import { useEventMembersRequest } from '../useEventMembersRequest'

enum steps {
  MAIN = 0,
  MEMBERS = 1,
  CHANEL_MANAGER = 2
}

const MAX_SELECTED_CHANNEL_MANAGER = 5

type TStepsMapProps = Pick<
  TEventCreateChannelMainStepProps,
  | 'channelId'
  | 'setName'
  | 'channelManagers'
  | 'isEdit'
  | 'setIsPublic'
  | 'loading'
  | 'eventId'
  | 'variant'
  | 'onEditSuccess'
  | 'dirty'
  | 'isPublic'
  | 'initialValues'
> &
  Pick<
    TEventCreateChannelMembersStepProps,
    'setChannelMembers' | 'channelMembers' | 'setChannelManagers'
  > & {
    name: string
    handleOpenMain: () => void
    handleOpenChanelManager: () => void
    handleOpenMembers: () => void
  }

const STEPS_MAP = new Map<steps, (props: TStepsMapProps) => JSX.Element>()
  .set(
    steps.MAIN,
    ({
      name,
      eventId,
      channelId,
      channelMembers,
      channelManagers,
      isEdit,
      loading,
      variant,
      dirty,
      isPublic,
      handleOpenChanelManager,
      handleOpenMembers,
      setName,
      setIsPublic,
      onEditSuccess,
      initialValues
    }) => (
      <EventCreateChannelMainStep
        dirty={dirty}
        variant={variant}
        eventId={eventId}
        name={name}
        channelId={channelId}
        channelMembers={channelMembers}
        channelManagers={channelManagers}
        setName={setName}
        onOpenChanelManager={handleOpenChanelManager}
        onOpenMembers={handleOpenMembers}
        isEdit={isEdit}
        loading={loading}
        onEditSuccess={onEditSuccess}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
        initialValues={initialValues}
      />
    )
  )
  .set(
    steps.MEMBERS,
    ({
      channelMembers,
      eventId,
      channelManagers,
      channelId,
      handleOpenMain,
      setChannelMembers,
      setChannelManagers
    }) => (
      <EventCreateChannelMembersStep
        variant="members"
        channelManagerIds={channelManagers?.map((manager) => manager.id)}
        eventId={eventId}
        channelMembers={channelMembers}
        markChatIds={channelId ? [channelId] : []}
        onBack={handleOpenMain}
        setChannelMembers={setChannelMembers}
        setChannelManagers={setChannelManagers}
      />
    )
  )
  .set(steps.CHANEL_MANAGER, ({ eventId, channelManagers, setChannelManagers, handleOpenMain }) => (
    <EventCreateChannelMembersStep
      variant="manager"
      maxSelected={MAX_SELECTED_CHANNEL_MANAGER}
      channelManagerIds={channelManagers?.map((manager) => manager.id)}
      eventId={eventId}
      channelMembers={channelManagers ? channelManagers : []}
      onBack={handleOpenMain}
      setChannelManagers={setChannelManagers}
    />
  ))

export type TEventCreateChannelDialogProps = Pick<
  TUseUnityHubCreateChannel,
  'channelId' | 'channelName' | 'isPublic'
> &
  Pick<TEventCreateChannelMainStepProps, 'onEditSuccess'> & {
    eventId: string
    variant?: Extract<TChannelVariant, 'chat' | 'news'>
  }

export const EventCreateChannelDialog = ({
  eventId,
  channelName,
  onEditSuccess,
  isPublic: channelIsPublic,
  channelId = '',
  variant = 'chat'
}: TEventCreateChannelDialogProps) => {
  const {
    name,
    channelMembers,
    channelManagers,
    step,
    isEdit,
    dirty,
    isPublic,
    setIsPublic,
    handleOpenMain,
    handleOpenMembers,
    handleOpenChanelManager,
    setName,
    setChannelMembers,
    setChannelManagers
  } = useUnityHubCreateChannel({
    channelName,
    channelId,
    isPublic: channelIsPublic,
    maxSelectedChannelManager: MAX_SELECTED_CHANNEL_MANAGER
  })

  const room = useAppSelector((state) => state.chat.chatRooms.list[channelId])

  const initialValues = useMemo(
    () => ({ name: channelName || '', isPublic: channelIsPublic || false }),
    [channelName, channelIsPublic]
  )

  const { isLoading, accounts } = useEventMembersRequest({
    variant,
    channelId,
    eventId,
    isEdit,
    setChannelMembers
  })

  useEffect(() => {
    if (isEdit && accounts?.length) {
      setChannelManagers(room.channelManagers ? room.channelManagers : undefined, true)
    }
  }, [accounts?.length, isEdit, room?.channelManagers, setChannelManagers])

  return (
    <>
      {getMapComponent(STEPS_MAP, step, {
        dirty,
        variant,
        name,
        channelMembers,
        channelManagers,
        channelId,
        isEdit,
        eventId,
        loading: isLoading,
        isPublic,
        initialValues,
        setIsPublic,
        setName,
        setChannelMembers,
        handleOpenMain,
        handleOpenMembers,
        handleOpenChanelManager,
        setChannelManagers,
        onEditSuccess
      })}
    </>
  )
}
